import { useTranslation } from 'react-i18next';
import type { DocumentStatus, DocumentType, PaymentStatus } from '@erp/shared';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const STATUS_STYLES: Record<DocumentStatus, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  SENT: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  ACCEPTED: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  REJECTED: 'bg-destructive/15 text-destructive',
  CONFIRMED: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  IN_PROGRESS: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  PARTIALLY_PROCESSED: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  SHIPPED: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  COMPLETED: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  POSTED: 'bg-primary/15 text-primary',
  CANCELLED: 'bg-destructive/15 text-destructive',
};

const PAYMENT_STYLES: Record<PaymentStatus, string> = {
  OPEN: 'bg-muted text-muted-foreground',
  PARTIALLY_PAID: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  PAID: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  OVERDUE: 'bg-destructive/15 text-destructive',
  WRITTEN_OFF: 'bg-muted text-muted-foreground line-through',
};

export function DocumentStatusBadge({ status }: { status: DocumentStatus }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className={cn('rounded px-2 py-0.5 text-xs font-medium', STATUS_STYLES[status])}>
      {t(`documents.statuses.${status}`)}
    </span>
  );
}

export function PaymentStatusBadge({ status }: { status: PaymentStatus }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className={cn('rounded px-2 py-0.5 text-xs font-medium', PAYMENT_STYLES[status])}>
      {t(`documents.paymentStatuses.${status}`)}
    </span>
  );
}

export function DocumentTypeBadge({ type }: { type: DocumentType }): JSX.Element {
  const { t } = useTranslation();
  return <Badge variant="outline">{t(`documents.types.${type}`)}</Badge>;
}
